import { useNavigation } from '@/app/hooks'
import { useAppSelector } from '@/hooks'
import { thermostatsCollection } from '@/reducers/thermostats'
import { vehicleChargersCollection } from '@/reducers/vehicleChargers'
import { useLocation, useParams } from 'react-router-dom'
import { usePrescribedChargers } from './usePrescribedChargers'
import { isNativeMobile } from '@/authenticated/hooks/useIsMobile'

export function useConfigureDeviceFlow() {
  const vehicles = useAppSelector((state) => state.vehicles.vehicles)
  const vehiclesLoading = useAppSelector((state) => state.vehicles.isLoading)
  const { data: thermostats, isLoadingOrIdle: thermostatsLoading } =
    thermostatsCollection.useFetch()
  const { data: chargers, isLoadingOrIdle: chargersLoading } =
    vehicleChargersCollection.useFetch()
  const { prescribedChargers, isLoading: prescribedChargersLoading } =
    usePrescribedChargers(chargers ?? [])
  const navigate = useNavigation()
  const location = useLocation()
  const { deviceId } = useParams<{ deviceId: string }>()
  const deviceIdsSearchParam = new URLSearchParams(location.search).get(
    'deviceIds',
  )
  const deviceIds = deviceIdsSearchParam ? deviceIdsSearchParam.split(',') : []
  const isLoading =
    vehiclesLoading ||
    thermostatsLoading ||
    chargersLoading ||
    prescribedChargersLoading
  const getSteps = () => {
    if (isLoading) {
      return null
    }
    const vehicleSteps = (vehicles ?? []).map((vehicle) => ({
      deviceId: vehicle.id,
      deviceType: ConfigureDeviceType.Vehicle,
      url: `/configure-devices/${vehicle.id}/vehicle`,
    }))
    const thermostatSteps = thermostats.map((thermostat) => ({
      deviceId: thermostat.id,
      deviceType: ConfigureDeviceType.Thermostat,
      url: `/configure-devices/${thermostat.id}/thermostat`,
    }))
    // DEV-6023: chargers are only included when the program prescribes a goal
    // for that charger. `usePrescribedChargers` does the filtering.
    const chargerSteps = prescribedChargers.map((charger) => ({
      deviceId: charger.id,
      deviceType: ConfigureDeviceType.Charger,
      url: `/configure-devices/${charger.id}/charger`,
    }))
    const mobileStep = {
      deviceId: 'mobile',
      deviceType: ConfigureDeviceType.Mobile,
      url: `/configure-devices/mobile/push-notifications`,
    }

    const steps = [
      ...vehicleSteps,
      ...thermostatSteps,
      ...chargerSteps,
      mobileStep,
    ].filter((d) => deviceIds.includes(d.deviceId.toString()))

    return steps
  }

  const steps = getSteps()
  const totalSteps = steps?.length
  const currentStepIndex = steps?.findIndex(
    (s) => s.deviceId.toString() === deviceId,
  )
  const completedSteps = currentStepIndex === -1 ? undefined : currentStepIndex
  const progress =
    completedSteps !== undefined && totalSteps !== undefined
      ? { totalSteps, completedSteps }
      : null

  const nextStep = () => {
    if (currentStepIndex === undefined) {
      return
    }
    const nextStep = steps?.[currentStepIndex + 1]
    if (nextStep) {
      navigate.pushRetainingParams(nextStep.url)
      return
    }
    navigate.push('/devices')
  }

  const initFlow = ({
    deviceIds,
    setupMobile,
  }: {
    deviceIds: (number | string)[]
    setupMobile: boolean
  }) => {
    // Skip the mobile-app-download step when the user is already inside the
    // native React Native WebView — they don't need to download the app.
    if (setupMobile && !isNativeMobile()) {
      deviceIds.push('mobile')
    }
    const searchParams = new URLSearchParams([['deviceIds', deviceIds.join()]])
    navigate.push(`/configure-devices/init?${searchParams.toString()}`)
  }

  return { deviceIds, steps, progress, nextStep, initFlow }
}

export enum ConfigureDeviceType {
  Vehicle = 'vehicle',
  Thermostat = 'thermostat',
  Charger = 'charger',
  Mobile = 'mobile',
}
